import './LinearNetworks.scss';
import cn from 'classnames';
import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { Toast, ToastContainer } from 'react-bootstrap';
import { MdAdd, MdGridView, MdViewSidebar } from 'react-icons/md';
import { LinearCanonicalNetworkEditedRow } from '@blisspointmedia/bpm-types/dist/LinearAssignments';
import { Button } from '@Components/Button';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { ModalEditWidget } from '@Components/ModalEditWidget/ModalEditWidget';
import {
  SaveChangesInput,
  SelectorOption,
  SelectorOptions
} from '@Components/ModalEditWidget/modal-edit-constants';
import { Page } from '@Components/Page';
import { SearchField } from '@Components/SearchField/SearchField';
import { FullPageSpinner } from '@Components/Spinner/Spinner';
import { VirtualizedGroupedList } from '@Components/VirtualizedGroupedList/VirtualizedGroupedList';
import { useAppSelector } from '@src/redux';
import { useSetError } from '@src/redux/modals';
import { emailSelector } from '@src/redux/user';
import { PAGE_NAMES } from '@Components/Page/page-constants';
import { Size } from '@Components/designConstants';
import { Mixpanel, MxE } from '../../utils/mixpanelWrapper';
import { AddNetworkModal } from './AddNetworkModal';
import { NetworkOverview } from './NetworkOverview';
import {
  AVAIL_OPTIONS,
  buildSearchBlobs,
  checkContactIsValid,
  colorOf,
  CONTACT_SELECTOR_STATICS,
  CONTACTS_HEADERS_BULK,
  CONTACTS_HEADERS_FOCUS,
  ContactRow,
  isNetworkPersistable,
  matchSearchBlobs,
  NETWORK_HEADERS,
  NETWORK_SELECTOR_OPTIONS,
  NetworkFilter,
  NetworkFilterType,
  networkGroupOf,
  NetworkSearchBlobs,
  normalizeNetworkCheckboxes,
  RATE_STATUS_OPTIONS,
  RATES_HEADERS_BULK,
  RATES_HEADERS_FOCUS,
  RatesRow,
  shortCodeOf,
  stationNameOf,
  ViewMode,
  ViewModeType,
  WorkspaceTab,
  WorkspaceTabType
} from './linear-networks-constants';
import {
  RateChanges,
  saveContacts,
  saveNetworks,
  saveRates,
  useCanonicalNetworks,
  useContacts,
  useMarketOptions,
  useRates
} from './use-linear-networks';

const TABS: { key: WorkspaceTabType; label: string }[] = [
  { key: WorkspaceTab.OVERVIEW, label: 'Overview' },
  { key: WorkspaceTab.RATES, label: 'Rates' },
  { key: WorkspaceTab.CONTACTS, label: 'Contacts' }
];

const FILTERS: { key: NetworkFilterType; label: string }[] = [
  { key: NetworkFilter.ALL, label: 'All' },
  { key: NetworkFilter.LINEAR, label: 'Linear' },
  { key: NetworkFilter.RADIO, label: 'Radio' }
];

interface DerivedNetwork {
  network: LinearCanonicalNetworkEditedRow;
  shortCode: string;
  stationName: string;
  group: string;
  channel: string;
  rateCount: number;
  contactCount: number;
  searchBlobs: NetworkSearchBlobs;
}

interface FilteredNetwork {
  derived: DerivedNetwork;
  hitTab: WorkspaceTabType | null;
}

export const LinearNetworks = () => {
  const setError = useSetError();
  const email = useAppSelector(emailSelector);

  const networksQuery = useCanonicalNetworks();
  const ratesQuery = useRates();
  const contactsQuery = useContacts();
  const marketQuery = useMarketOptions();

  const [selectedShortCode, setSelectedShortCode] = useState<string>();
  const [activeTab, setActiveTab] = useState<WorkspaceTabType>(WorkspaceTab.OVERVIEW);
  const [viewMode, setViewMode] = useState<ViewModeType>(ViewMode.FOCUS);
  const [bulkEntity, setBulkEntity] = useState<WorkspaceTabType>(WorkspaceTab.RATES);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<NetworkFilterType>(NetworkFilter.ALL);
  const [showAddModal, setShowAddModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toastMessage, setToastMessage] = useState<string>();

  // Deprioritize the filter pass so typing stays responsive (the input updates immediately; the
  // filtered list catches up on a lower-priority render).
  const deferredQuery = useDeferredValue(query);

  const networks = useMemo(() => networksQuery.data ?? [], [networksQuery.data]);
  const rates = useMemo(() => ratesQuery.data ?? [], [ratesQuery.data]);
  const contacts = useMemo<ContactRow[]>(
    () => contactsQuery.data?.contacts ?? [],
    [contactsQuery.data]
  );

  const queryError =
    networksQuery.error || ratesQuery.error || contactsQuery.error || marketQuery.error;
  useEffect(() => {
    if (queryError) {
      setError({
        title: "Couldn't load linear network metadata",
        message: `Failed to load data: ${queryError.message}`,
        reportError: queryError
      });
    }
  }, [queryError, setError]);

  // rates keyed by the canonical short code they belong to; contacts keyed by station name.
  const ratesByCode = useMemo(() => {
    const map = new Map<string, RatesRow[]>();
    rates.forEach(rate => {
      const list = map.get(rate.network) ?? [];
      list.push(rate);
      map.set(rate.network, list);
    });
    return map;
  }, [rates]);

  const contactsByStation = useMemo(() => {
    const map = new Map<string, ContactRow[]>();
    contacts.forEach(contact => {
      const list = map.get(contact['Canonical Station']) ?? [];
      list.push(contact);
      map.set(contact['Canonical Station'], list);
    });
    return map;
  }, [contacts]);

  const stationByShortCode = useMemo(() => {
    const map = new Map<string, string>();
    networks.forEach(network => map.set(shortCodeOf(network), stationNameOf(network)));
    return map;
  }, [networks]);

  const shortCodeByStation = useMemo(() => {
    const map = new Map<string, string>();
    networks.forEach(network => map.set(stationNameOf(network), shortCodeOf(network)));
    return map;
  }, [networks]);

  const derivedByCode = useMemo(() => {
    const map = new Map<string, DerivedNetwork>();
    networks.forEach(network => {
      const shortCode = shortCodeOf(network);
      const stationName = stationNameOf(network);
      const netRates = ratesByCode.get(shortCode) ?? [];
      const netContacts = contactsByStation.get(stationName) ?? [];
      map.set(shortCode, {
        network,
        shortCode,
        stationName,
        group: networkGroupOf(network),
        channel: network.channel ?? 'Linear',
        rateCount: netRates.length,
        contactCount: netContacts.length,
        searchBlobs: buildSearchBlobs(network, netRates, netContacts)
      });
    });
    return map;
  }, [networks, ratesByCode, contactsByStation]);

  const filteredNetworks = useMemo<FilteredNetwork[]>(
    () =>
      [...derivedByCode.values()]
        .filter(derived => {
          if (filter === NetworkFilter.LINEAR && derived.channel !== 'Linear') return false;
          if (filter === NetworkFilter.RADIO && derived.channel !== 'Radio') return false;
          return true;
        })
        .map(derived => ({ derived, match: matchSearchBlobs(derived.searchBlobs, deferredQuery) }))
        .filter(entry => entry.match.matched)
        .map(entry => ({ derived: entry.derived, hitTab: entry.match.hitTab }))
        .sort(
          (a, b) =>
            a.derived.group.localeCompare(b.derived.group) ||
            a.derived.stationName.localeCompare(b.derived.stationName)
        ),
    [derivedByCode, filter, deferredQuery]
  );

  // distinct, real network groups for the Add Network combobox (excludes the 'Ungrouped' fallback)
  const networkGroups = useMemo(
    () => [...new Set(networks.map(networkGroupOf).filter(group => group !== 'Ungrouped'))].sort(),
    [networks]
  );

  // default the selection to the first network once data lands
  useEffect(() => {
    if (!selectedShortCode && networks.length) {
      setSelectedShortCode(shortCodeOf(networks[0]));
    }
  }, [networks, selectedShortCode]);

  const selectedDerived = selectedShortCode ? derivedByCode.get(selectedShortCode) : undefined;

  // ---- selector options shared with the edit widgets ----
  const networkCodeOptions = useMemo<SelectorOption[]>(
    () =>
      networks
        .map(shortCodeOf)
        .filter(Boolean)
        .map(code => ({ label: code, value: code })),
    [networks]
  );
  const stationOptions = useMemo<SelectorOption[]>(
    () =>
      networks
        .map(stationNameOf)
        .filter(Boolean)
        .map(station => ({ label: station, value: station })),
    [networks]
  );
  const marketOptions = useMemo<SelectorOption[]>(
    () => (marketQuery.data ?? []).map(market => ({ label: market, value: market })),
    [marketQuery.data]
  );

  const ratesSelectorOptions = useMemo<SelectorOptions>(
    () => ({
      availOptions: AVAIL_OPTIONS,
      statusOptions: RATE_STATUS_OPTIONS,
      marketOptions,
      networkCode: networkCodeOptions
    }),
    [marketOptions, networkCodeOptions]
  );
  const contactsSelectorOptions = useMemo<SelectorOptions>(
    () => ({ ...CONTACT_SELECTOR_STATICS, station: stationOptions }),
    [stationOptions]
  );

  // ---- save handlers ----
  const persist = async (
    save: () => Promise<void>,
    refetch: () => Promise<unknown>,
    cleanup: () => void
  ): Promise<boolean> => {
    setSaving(true);
    let success = false;
    try {
      await save();
      await refetch();
      cleanup();
      setToastMessage('Changes saved');
      success = true;
    } catch (e) {
      setError({ message: `Save failed: ${e.message}`, reportError: e });
    }
    setSaving(false);
    return success;
  };

  // `deletedData` is intentionally ignored: /linear_canonical_networks only upserts, so there is no
  // way to remove a canonical network from here.
  const handleSaveNetworks = async ({
    addedData,
    modifiedData,
    cleanup
  }: SaveChangesInput<LinearCanonicalNetworkEditedRow>) => {
    const changed = [...addedData, ...modifiedData]
      .filter(isNetworkPersistable)
      .map(normalizeNetworkCheckboxes);
    if (await persist(() => saveNetworks(changed), networksQuery.refetch, cleanup)) {
      Mixpanel.track(MxE.RecordSaved, { page: PAGE_NAMES.LINEAR_NETWORKS, entity: 'network' });
    }
  };

  const handleSaveRates = async ({
    addedData,
    modifiedData,
    deletedData,
    cleanup
  }: SaveChangesInput<RatesRow>) => {
    const added: Record<string, RatesRow> = {};
    addedData.forEach((rate, index) => {
      const id = -(index + 1);
      added[id] = {
        ...rate,
        id,
        full_network: stationByShortCode.get(rate.network) || rate.full_network
      };
    });
    const updated: Record<string, RatesRow> = {};
    modifiedData.forEach(rate => {
      if (rate.id != null) {
        updated[rate.id] = rate;
      }
    });
    const removed: Record<string, RatesRow> = {};
    deletedData.forEach(rate => {
      if (rate.id != null) {
        removed[rate.id] = rate;
      }
    });
    const changes: RateChanges = { added, removed, updated };
    if (await persist(() => saveRates(changes, email), ratesQuery.refetch, cleanup)) {
      Mixpanel.track(MxE.RecordSaved, { page: PAGE_NAMES.LINEAR_NETWORKS, entity: 'rate' });
    }
  };

  const handleSaveContacts = async ({
    addedData,
    modifiedData,
    deletedData,
    cleanup
  }: SaveChangesInput<ContactRow>) => {
    const withDerivedCode = (contact: ContactRow): ContactRow => ({
      ...contact,
      'Short Code': shortCodeByStation.get(contact['Canonical Station']) || contact['Short Code']
    });
    if (
      await persist(
        () =>
          saveContacts({
            insert: addedData.map(withDerivedCode),
            update: modifiedData.map(withDerivedCode),
            delete: deletedData
          }),
        contactsQuery.refetch,
        cleanup
      )
    ) {
      Mixpanel.track(MxE.RecordSaved, { page: PAGE_NAMES.LINEAR_NETWORKS, entity: 'contact' });
    }
  };

  const handleCreateNetwork = async (network: LinearCanonicalNetworkEditedRow) => {
    setSaving(true);
    try {
      await saveNetworks([network]);
      await networksQuery.refetch();
      setShowAddModal(false);
      setSelectedShortCode(shortCodeOf(network));
      setActiveTab(WorkspaceTab.OVERVIEW);
      setViewMode(ViewMode.FOCUS);
      setToastMessage('Network added');
      Mixpanel.track(MxE.RecordCreated, { page: PAGE_NAMES.LINEAR_NETWORKS, entity: 'network' });
    } catch (e) {
      setError({ message: `Couldn't add network: ${e.message}`, reportError: e });
    }
    setSaving(false);
  };

  // single-network edit from the Overview tab (no widget diff — just upsert the one row)
  const handleUpdateNetwork = (updated: LinearCanonicalNetworkEditedRow) =>
    persist(
      () => saveNetworks([updated].filter(isNetworkPersistable)),
      networksQuery.refetch,
      () => {}
    );

  const handleSelect = (shortCode: string, hitTab: WorkspaceTabType | null) => {
    setSelectedShortCode(shortCode);
    if (hitTab) {
      setActiveTab(hitTab);
    }
  };

  const isLoading = networksQuery.isLoading || ratesQuery.isLoading || contactsQuery.isLoading;

  return (
    <Page
      title={PAGE_NAMES.LINEAR_NETWORKS}
      pageType={PAGE_NAMES.LINEAR_NETWORKS}
      minHeight='640px'
      isLoading={isLoading}
      actions={
        <WorkspaceActions
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          onAddNetwork={() => {
            setShowAddModal(true);
            Mixpanel.track(MxE.ModalOpened, {
              page: PAGE_NAMES.LINEAR_NETWORKS,
              modal: 'add_network'
            });
          }}
        />
      }
    >
      {isLoading ? (
        <FullPageSpinner />
      ) : (
        <div className={cn('linear-networks', { saving })}>
          {viewMode === ViewMode.FOCUS ? (
            <div className='lnm-workspace'>
              <NetworkList
                items={filteredNetworks}
                total={networks.length}
                selectedShortCode={selectedShortCode}
                query={query}
                filter={filter}
                onQueryChange={setQuery}
                onFilterChange={setFilter}
                onSelect={handleSelect}
              />
              <DetailPanel
                derived={selectedDerived}
                activeTab={activeTab}
                ratesSelectorOptions={ratesSelectorOptions}
                contactsSelectorOptions={contactsSelectorOptions}
                ratesForNetwork={
                  selectedDerived ? (ratesByCode.get(selectedDerived.shortCode) ?? []) : []
                }
                contactsForNetwork={
                  selectedDerived ? (contactsByStation.get(selectedDerived.stationName) ?? []) : []
                }
                saving={saving}
                networkGroups={networkGroups}
                onTabChange={tab => {
                  setActiveTab(tab);
                  Mixpanel.track(MxE.TabChange, {
                    page: PAGE_NAMES.LINEAR_NETWORKS,
                    option_selected: tab
                  });
                }}
                onUpdateNetwork={handleUpdateNetwork}
                onSaveRates={handleSaveRates}
                onSaveContacts={handleSaveContacts}
              />
            </div>
          ) : (
            <BulkGrid
              bulkEntity={bulkEntity}
              networks={networks}
              rates={rates}
              contacts={contacts}
              ratesSelectorOptions={ratesSelectorOptions}
              contactsSelectorOptions={contactsSelectorOptions}
              onEntityChange={entity => {
                setBulkEntity(entity);
                Mixpanel.track(MxE.TabChange, {
                  page: PAGE_NAMES.LINEAR_NETWORKS,
                  option_selected: entity
                });
              }}
              onSaveNetworks={handleSaveNetworks}
              onSaveRates={handleSaveRates}
              onSaveContacts={handleSaveContacts}
            />
          )}
        </div>
      )}
      <AddNetworkModal
        show={showAddModal}
        saving={saving}
        existingShortCodes={networks.map(shortCodeOf)}
        existingGroups={networkGroups}
        onHide={() => setShowAddModal(false)}
        onCreate={handleCreateNetwork}
      />
      <ToastContainer
        position='top-center'
        containerPosition='fixed'
        className='lnm-toast-container'
      >
        <Toast
          className='lnm-toast'
          show={Boolean(toastMessage)}
          onClose={() => setToastMessage(undefined)}
          delay={2500}
          autohide
        >
          <Toast.Body>{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
    </Page>
  );
};

// ---- page header actions ----

interface WorkspaceActionsProps {
  viewMode: ViewModeType;
  onViewModeChange: (mode: ViewModeType) => void;
  onAddNetwork: () => void;
}

const WorkspaceActions = ({ viewMode, onViewModeChange, onAddNetwork }: WorkspaceActionsProps) => (
  <div className='lnm-actions'>
    <div className='lnm-mode-toggle'>
      <InteractiveDiv
        className={cn('lnm-mode-btn', { active: viewMode === ViewMode.FOCUS })}
        onClick={() => onViewModeChange(ViewMode.FOCUS)}
      >
        <MdViewSidebar />
        Detail
      </InteractiveDiv>
      <InteractiveDiv
        className={cn('lnm-mode-btn', { active: viewMode === ViewMode.BULK })}
        onClick={() => onViewModeChange(ViewMode.BULK)}
      >
        <MdGridView />
        Grid
      </InteractiveDiv>
    </div>
    <Button size={Size.LG} leadingElement={<MdAdd />} onClick={onAddNetwork}>
      Add network
    </Button>
  </div>
);

// ---- master list ----

interface NetworkListProps {
  items: FilteredNetwork[];
  total: number;
  selectedShortCode?: string;
  query: string;
  filter: NetworkFilterType;
  onQueryChange: (value: string) => void;
  onFilterChange: (filter: NetworkFilterType) => void;
  onSelect: (shortCode: string, hitTab: WorkspaceTabType | null) => void;
}

const groupOfFilteredNetwork = (item: FilteredNetwork) => item.derived.group;
const keyOfFilteredNetwork = (item: FilteredNetwork) => item.derived.shortCode;

const NetworkList = ({
  items,
  total,
  selectedShortCode,
  query,
  filter,
  onQueryChange,
  onFilterChange,
  onSelect
}: NetworkListProps) => (
  <div className='lnm-master'>
    <div className='lnm-master-head'>
      <div className='lnm-master-title'>
        <h2>Networks</h2>
        <span className='lnm-count'>{total}</span>
      </div>
      <SearchField
        size={Size.LG}
        value={query}
        placeholder='Search networks, rotations, contacts…'
        onChange={event => onQueryChange(event.target.value)}
      />
      <div className='lnm-filters'>
        {FILTERS.map(option => (
          <InteractiveDiv
            key={option.key}
            className={cn('lnm-chip', { active: filter === option.key })}
            onClick={() => onFilterChange(option.key)}
          >
            {option.label}
          </InteractiveDiv>
        ))}
      </div>
    </div>
    <div className='lnm-netlist'>
      <VirtualizedGroupedList
        items={items}
        groupOf={groupOfFilteredNetwork}
        keyOf={keyOfFilteredNetwork}
        itemHeight={48}
        groupLabelClassName='lnm-group-label'
        groupCountClassName='lnm-group-count'
        emptyClassName='lnm-empty'
        emptyMessage='No networks match.'
        renderItem={item => (
          <NetworkListItem
            item={item}
            selected={item.derived.shortCode === selectedShortCode}
            onSelect={onSelect}
          />
        )}
      />
    </div>
  </div>
);

interface NetworkListItemProps {
  item: FilteredNetwork;
  selected: boolean;
  onSelect: (shortCode: string, hitTab: WorkspaceTabType | null) => void;
}

const NetworkListItem = ({ item, selected, onSelect }: NetworkListItemProps) => {
  const { derived, hitTab } = item;
  return (
    <InteractiveDiv
      className={cn('lnm-net-item', { active: selected })}
      onClick={() => onSelect(derived.shortCode, hitTab)}
    >
      <span
        className='lnm-swatch'
        style={{ backgroundColor: colorOf(derived.network) ?? 'transparent' }}
      />
      <span className='lnm-net-main'>
        <span className='lnm-net-name'>{derived.stationName || derived.shortCode}</span>
        <span className='lnm-net-sub'>
          <span>{derived.shortCode}</span>
          <span>{derived.channel}</span>
        </span>
      </span>
      <span className='lnm-badges'>
        <span className='lnm-badge'>{derived.rateCount}R</span>
        <span className='lnm-badge'>{derived.contactCount}C</span>
      </span>
    </InteractiveDiv>
  );
};

// ---- detail panel (focus mode) ----

interface DetailPanelProps {
  derived?: DerivedNetwork;
  activeTab: WorkspaceTabType;
  ratesSelectorOptions: SelectorOptions;
  contactsSelectorOptions: SelectorOptions;
  ratesForNetwork: RatesRow[];
  contactsForNetwork: ContactRow[];
  saving: boolean;
  networkGroups: string[];
  onTabChange: (tab: WorkspaceTabType) => void;
  onUpdateNetwork: (updated: LinearCanonicalNetworkEditedRow) => Promise<boolean>;
  onSaveRates: (input: SaveChangesInput<RatesRow>) => Promise<void>;
  onSaveContacts: (input: SaveChangesInput<ContactRow>) => Promise<void>;
}

const DetailPanel = ({
  derived,
  activeTab,
  ratesSelectorOptions,
  contactsSelectorOptions,
  ratesForNetwork,
  contactsForNetwork,
  saving,
  networkGroups,
  onTabChange,
  onUpdateNetwork,
  onSaveRates,
  onSaveContacts
}: DetailPanelProps) => {
  if (!derived) {
    return <div className='lnm-detail lnm-empty'>Select a network to see its metadata.</div>;
  }

  const { network, shortCode, stationName, channel } = derived;

  return (
    <div className='lnm-detail'>
      <div className='lnm-detail-head'>
        <span
          className='lnm-detail-swatch'
          style={{ backgroundColor: colorOf(network) ?? 'transparent' }}
        />
        <div className='lnm-detail-title'>
          <h3>{stationName || shortCode}</h3>
          <div className='lnm-pills'>
            <span className='lnm-pill mono'>{shortCode}</span>
            <span className='lnm-pill accent'>{channel}</span>
          </div>
        </div>
      </div>
      <div className='lnm-tabs'>
        {TABS.map(tab => (
          <InteractiveDiv
            key={tab.key}
            className={cn('lnm-tab', { active: activeTab === tab.key })}
            onClick={() => onTabChange(tab.key)}
          >
            {tab.label}
            {tab.key === WorkspaceTab.RATES && (
              <span className='lnm-tab-n'>{ratesForNetwork.length}</span>
            )}
            {tab.key === WorkspaceTab.CONTACTS && (
              <span className='lnm-tab-n'>{contactsForNetwork.length}</span>
            )}
          </InteractiveDiv>
        ))}
      </div>
      <div className='lnm-detail-body'>
        {activeTab === WorkspaceTab.OVERVIEW && (
          <NetworkOverview
            key={shortCode}
            network={network}
            saving={saving}
            networkGroups={networkGroups}
            onSave={onUpdateNetwork}
          />
        )}
        {activeTab === WorkspaceTab.RATES && (
          <ModalEditWidget<RatesRow>
            saveOnEdit
            className='lnm-table'
            name={`Rates — ${stationName}`}
            headers={RATES_HEADERS_FOCUS}
            selectorOptions={ratesSelectorOptions}
            initialTableData={ratesForNetwork}
            defaultNewRow={{ network: shortCode, full_network: stationName }}
            filterBar
            onSaveModal={onSaveRates}
            dataTestId='network-rates-table'
          />
        )}
        {activeTab === WorkspaceTab.CONTACTS && (
          <ModalEditWidget<ContactRow>
            saveOnEdit
            className='lnm-table'
            name={`Contacts — ${stationName}`}
            headers={CONTACTS_HEADERS_FOCUS}
            checkIsValid={checkContactIsValid}
            selectorOptions={contactsSelectorOptions}
            initialTableData={contactsForNetwork}
            defaultNewRow={{ 'Canonical Station': stationName, 'Short Code': shortCode }}
            filterBar
            onSaveModal={onSaveContacts}
            dataTestId='network-contacts-table'
          />
        )}
      </div>
    </div>
  );
};

// ---- bulk grid (power editing across all networks) ----

interface BulkGridProps {
  bulkEntity: WorkspaceTabType;
  networks: LinearCanonicalNetworkEditedRow[];
  rates: RatesRow[];
  contacts: ContactRow[];
  ratesSelectorOptions: SelectorOptions;
  contactsSelectorOptions: SelectorOptions;
  onEntityChange: (entity: WorkspaceTabType) => void;
  onSaveNetworks: (input: SaveChangesInput<LinearCanonicalNetworkEditedRow>) => Promise<void>;
  onSaveRates: (input: SaveChangesInput<RatesRow>) => Promise<void>;
  onSaveContacts: (input: SaveChangesInput<ContactRow>) => Promise<void>;
}

const BulkGrid = ({
  bulkEntity,
  networks,
  rates,
  contacts,
  ratesSelectorOptions,
  contactsSelectorOptions,
  onEntityChange,
  onSaveNetworks,
  onSaveRates,
  onSaveContacts
}: BulkGridProps) => (
  <div className='lnm-bulk'>
    <div className='lnm-tabs'>
      {TABS.map(tab => (
        <InteractiveDiv
          key={tab.key}
          className={cn('lnm-tab', { active: bulkEntity === tab.key })}
          onClick={() => onEntityChange(tab.key)}
        >
          {tab.label === 'Overview' ? 'Networks' : tab.label}
        </InteractiveDiv>
      ))}
    </div>
    <div className='lnm-bulk-body'>
      {bulkEntity === WorkspaceTab.OVERVIEW && (
        <ModalEditWidget<LinearCanonicalNetworkEditedRow>
          className='lnm-table'
          name='All networks'
          headers={NETWORK_HEADERS}
          selectorOptions={NETWORK_SELECTOR_OPTIONS}
          initialTableData={networks}
          filterBar
          onSaveModal={onSaveNetworks}
          dataTestId='bulk-networks-table'
        />
      )}
      {bulkEntity === WorkspaceTab.RATES && (
        <ModalEditWidget<RatesRow>
          className='lnm-table'
          name='All rates'
          headers={RATES_HEADERS_BULK}
          selectorOptions={ratesSelectorOptions}
          initialTableData={rates}
          filterBar
          onSaveModal={onSaveRates}
          dataTestId='bulk-rates-table'
        />
      )}
      {bulkEntity === WorkspaceTab.CONTACTS && (
        <ModalEditWidget<ContactRow>
          className='lnm-table'
          name='All contacts'
          headers={CONTACTS_HEADERS_BULK}
          checkIsValid={checkContactIsValid}
          selectorOptions={contactsSelectorOptions}
          initialTableData={contacts}
          filterBar
          onSaveModal={onSaveContacts}
          dataTestId='bulk-contacts-table'
        />
      )}
    </div>
  </div>
);
